/**
 * Blood Test header scroll behaviour.
 *
 * Mirrors the homepage header (js/components/header-mega-menu.js):
 *   - sticky hide/reveal: hides on scroll down past a small threshold, reveals on scroll up
 *   - overlay -> solid flip: the bar stays glass while it overlays the hero and flips to a
 *     solid background once the hero's bottom edge reaches the bar's bottom edge
 *
 * Both behaviours are opt-in via data attributes written by the section, so the merchant
 * toggles in the theme editor govern them.
 */

const SCROLL_THRESHOLD = 50; // hide/reveal trigger — small, independent of hero height
const HIDDEN_CLASS = 'blood-test-header--hidden';
const SCROLLED_CLASS = 'blood-test-header--scrolled';
const FALLBACK_HEADER_HEIGHT = 78;

export default function initBloodTestHeaderScroll() {
  const root = document.querySelector('[data-blood-test-header]');
  if (!root) return null;

  const isSticky = root.dataset.stickyHide === 'true';
  const isOverlay = root.dataset.scrollFlip === 'true';
  if (!isSticky && !isOverlay) return null;

  // The hero is the first child of the layout's main content region.
  const heroSection = document.querySelector('#MainContent')?.firstElementChild || null;

  let lastY = window.scrollY;
  let rafPending = false;
  let flipThreshold = SCROLL_THRESHOLD;
  let resizeObserver = null;

  // Hero height is image-driven, so measure it live and keep it fresh — the image
  // finishing its load, a viewport resize, or an editor swap all change it.
  const measureFlipThreshold = () => {
    if (!heroSection) return;
    const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
    const headerHeight = root.offsetHeight || FALLBACK_HEADER_HEIGHT;
    flipThreshold = Math.max(SCROLL_THRESHOLD, heroBottom - headerHeight);
  };

  const onScroll = () => {
    if (rafPending) return;
    rafPending = true;
    window.requestAnimationFrame(() => {
      rafPending = false;
      const y = window.scrollY;

      if (isSticky) {
        if (y > SCROLL_THRESHOLD && y > lastY) {
          root.classList.add(HIDDEN_CLASS);
        } else if (y < lastY) {
          root.classList.remove(HIDDEN_CLASS);
        }
      }

      if (isOverlay) {
        root.classList.toggle(SCROLLED_CLASS, y > flipThreshold);
      }

      lastY = y;
    });
  };

  if (isOverlay && heroSection) {
    measureFlipThreshold();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(measureFlipThreshold);
      resizeObserver.observe(heroSection);
    }
    window.addEventListener('resize', measureFlipThreshold, { passive: true });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll(); // settle the initial state for deep links / restored scroll positions

  return {
    destroy() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measureFlipThreshold);
      if (resizeObserver) resizeObserver.disconnect();
      root.classList.remove(HIDDEN_CLASS, SCROLLED_CLASS);
    },
  };
}
